/**
 * Unified Billing Interface - Parent Portal Refactor
 * 
 * CREATED FOR: Parent Portal refactor & redesign (Phase 3.2)
 * PURPOSE: Optimize checkout flows and billing for mobile completion
 * TECH DEBT: Replaces basic billing display with mobile-optimized interface
 * 
 * This component addresses critical tech debt by:
 * - Creating mobile-first payment interfaces that are touch-friendly
 * - Implementing streamlined checkout flows optimized for mobile completion
 * - Using new responsive design tokens throughout
 * - Adding mobile-optimized invoice viewing and payment management
 * - Enhanced payment method management with proper mobile UX
 * - Quick payment actions for common billing tasks
 * 
 * Key features:
 * - Touch-optimized payment interfaces with proper form validation
 * - Progressive disclosure for payment details and invoice information
 * - Mobile-first checkout flows with step-by-step guidance
 * - Quick action buttons for common billing tasks
 * - Responsive payment history with mobile-friendly table alternatives
 * - Enhanced payment method management with card scanning support
 */

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, 
  DollarSign, 
  Calendar, 
  Download, 
  Plus, 
  Eye,
  EyeOff,
  Check,
  Clock,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Receipt,
  Wallet,
  Smartphone,
  Shield,
  ArrowRight,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ParentLoadingState, ParentSkeletonList } from '@/components/parent-portal/ParentLoadingState';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useResponsive } from '@/hooks/useResponsive';
import { format, isValid } from 'date-fns';
import { 
  RESPONSIVE_SPACING, 
  RESPONSIVE_TYPOGRAPHY, 
  RESPONSIVE_ANIMATIONS,
  TOUCH_TARGETS 
} from '@/design-tokens/responsive';

// Safe date formatting function to prevent "invalid time value" errors
const safeFormatDate = (dateString: string | null | undefined, formatString: string, fallback: string = 'Invalid date'): string => {
  if (!dateString) return fallback;
  
  try {
    const date = new Date(dateString);
    if (isValid(date)) {
      return format(date, formatString);
    }
    return fallback;
  } catch (error) {
    console.warn('Date formatting error:', error, 'for date:', dateString);
    return fallback;
  }
};

// Types
interface PaymentMethod {
  id: string;
  type: 'card' | 'bank' | 'digital';
  last4: string;
  brand: string;
  expiryMonth: number;
  expiryYear: number;
  isDefault: boolean;
}

interface Invoice {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'processing';
  dueDate?: string;
  method?: string;
  downloadUrl?: string;
}

interface BillingStats {
  totalPaid: number;
  totalPending: number;
  nextPaymentDate?: string;
  nextPaymentAmount?: number;
}

interface UnifiedBillingInterfaceProps {
  paymentMethods: PaymentMethod[];
  invoices: Invoice[];
  stats: BillingStats;
  onAddPaymentMethod: () => void;
  onMakePayment: (invoiceId: string) => void;
  onDownloadInvoice: (invoiceId: string) => void;
  onSetDefaultPayment: (methodId: string) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

/**
 * Mobile-Optimized Payment Method Card
 */
const PaymentMethodCard: React.FC<{
  method: PaymentMethod;
  onSetDefault: () => void;
  onRemove?: () => void;
}> = ({ method, onSetDefault, onRemove }) => {
  const { isMobile } = useResponsive();

  const getCardIcon = (brand: string) => {
    // You could add actual card brand icons here
    return <CreditCard className="w-6 h-6" />;
  };

  const getCardColor = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa': return 'from-blue-600 to-blue-700';
      case 'mastercard': return 'from-red-600 to-red-700';
      case 'amex': return 'from-green-600 to-green-700';
      default: return 'from-gray-600 to-gray-700';
    }
  };

  return (
    <motion.div
      className={cn(
        "relative overflow-hidden rounded-2xl",
        `bg-gradient-to-br ${getCardColor(method.brand)}`,
        "text-white shadow-lg"
      )}
      whileHover={!isMobile ? { scale: 1.02 } : {}}
      whileTap={{ scale: 0.98 }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
      <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full transform translate-x-10 -translate-y-10" />
      
      <div className={cn(
        "relative z-10",
        RESPONSIVE_SPACING.card.padding
      )}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {getCardIcon(method.brand)}
            <span className="font-semibold capitalize">{method.brand}</span>
          </div>
          {method.isDefault && (
            <Badge className="bg-white/20 text-white border-white/40">
              Default
            </Badge>
          )}
        </div>
        
        <div className="space-y-2">
          <p className="text-2xl font-mono tracking-wider">
            •••• •••• •••• {method.last4}
          </p>
          <p className="text-white/80 text-sm">
            Expires {method.expiryMonth.toString().padStart(2, '0')}/{method.expiryYear}
          </p>
        </div>
        
        <div className={cn(
          "flex gap-2 mt-4",
          isMobile ? "flex-col" : "flex-row"
        )}>
          {!method.isDefault && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSetDefault}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              style={{ minHeight: TOUCH_TARGETS.minSize }}
            >
              Set as Default
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
            style={{ minHeight: TOUCH_TARGETS.minSize }}
          >
            Manage
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Mobile-Optimized Invoice Card
 */
const InvoiceCard: React.FC<{
  invoice: Invoice;
  onPay?: () => void;
  onDownload?: () => void;
  onViewDetails?: () => void;
}> = ({ invoice, onPay, onDownload, onViewDetails }) => {
  const { isMobile } = useResponsive();
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'overdue': return 'bg-red-100 text-red-700 border-red-200';
      case 'processing': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <Check className="w-3 h-3" />;
      case 'pending': return <Clock className="w-3 h-3" />;
      case 'overdue': return <AlertTriangle className="w-3 h-3" />;
      case 'processing': return <Clock className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };

  const isActionable = invoice.status === 'pending' || invoice.status === 'overdue';

  return (
    <motion.div
      className={cn(
        "bg-white border rounded-2xl shadow-sm overflow-hidden",
        isActionable && "border-l-4 border-l-amber-400",
        invoice.status === 'overdue' && "border-l-red-400"
      )}
      whileTap={{ scale: 0.98 }}
      layout
    >
      <div 
        className={cn(
          "cursor-pointer",
          RESPONSIVE_SPACING.card.padding
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className={cn(
                "font-semibold text-gray-900 truncate",
                RESPONSIVE_TYPOGRAPHY.bodyLarge
              )}>
                {invoice.description}
              </h3>
              <Badge 
                variant="outline" 
                className={cn("text-xs border", getStatusColor(invoice.status))}
              >
                <div className="flex items-center gap-1">
                  {getStatusIcon(invoice.status)}
                  <span className="capitalize">{invoice.status}</span>
                </div>
              </Badge>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className={cn(
                  "text-gray-600",
                  RESPONSIVE_TYPOGRAPHY.bodySmall
                )}>
                  {safeFormatDate(invoice.date, 'MMM d, yyyy', 'No date')}
                </p>
                {invoice.dueDate && invoice.status !== 'paid' && (
                  <p className={cn(
                    "text-gray-500",
                    RESPONSIVE_TYPOGRAPHY.caption
                  )}>
                    Due: {safeFormatDate(invoice.dueDate, 'MMM d, yyyy', 'No due date')}
                  </p>
                )}
              </div>
              
              <div className="text-right">
                <p className={cn(
                  "font-bold text-gray-900",
                  isMobile ? "text-lg" : "text-xl"
                )}>
                  ${invoice.amount.toFixed(2)}
                </p>
                {invoice.method && invoice.status === 'paid' && (
                  <p className={cn(
                    "text-gray-500",
                    RESPONSIVE_TYPOGRAPHY.caption
                  )}>
                    {invoice.method}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          <ChevronDown className={cn(
            "w-5 h-5 text-gray-400 ml-3 transition-transform",
            isExpanded && "rotate-180"
          )} />
        </div>
      </div>

      {/* Expanded Actions */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-gray-100"
          >
            <div className={cn(
              "grid gap-2",
              RESPONSIVE_SPACING.container.mobile,
              "py-4",
              isMobile ? "grid-cols-1" : "grid-cols-3"
            )}>
              {isActionable && onPay && (
                <Button 
                  onClick={onPay}
                  className="w-full"
                  style={{ minHeight: TOUCH_TARGETS.minSize }}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay Now
                </Button>
              )}
              
              {onDownload && (
                <Button 
                  variant="outline"
                  onClick={onDownload}
                  className="w-full"
                  style={{ minHeight: TOUCH_TARGETS.minSize }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              )}
              
              {onViewDetails && (
                <Button 
                  variant="ghost"
                  onClick={onViewDetails}
                  className="w-full"
                  style={{ minHeight: TOUCH_TARGETS.minSize }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Details
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * Billing Stats Header Component
 */
const BillingStatsHeader: React.FC<{
  stats: BillingStats;
}> = ({ stats }) => {
  const { isMobile } = useResponsive();

  const statsData = [
    {
      label: 'Total Paid',
      value: `$${stats.totalPaid.toFixed(2)}`,
      icon: Check,
      color: 'text-emerald-600'
    },
    {
      label: 'Total Due',
      value: `$${stats.totalPending.toFixed(2)}`,
      icon: Clock,
      color: 'text-amber-600'
    },
    {
      label: 'Next Payment',
      value: stats.nextPaymentDate ? safeFormatDate(stats.nextPaymentDate, 'MMM d', 'None') : 'None',
      icon: Calendar,
      color: 'text-blue-600'
    }
  ];

  return (
    <motion.div
      className={cn(
        "bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 rounded-2xl shadow-xl text-white overflow-hidden relative",
        RESPONSIVE_SPACING.card.padding
      )}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full transform translate-x-16 -translate-y-16" />
      
      <div className="relative z-10">
        <h1 className={cn(
          "font-bold text-white mb-6",
          RESPONSIVE_TYPOGRAPHY.h2
        )}>
          Billing & Payments
        </h1>
        
        <div className={cn(
          "grid gap-4",
          isMobile ? "grid-cols-1" : "grid-cols-3"
        )}>
          {statsData.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.4 }}
            >
              <div className="flex items-center gap-3">
                <stat.icon className="w-6 h-6 text-purple-200" />
                <div>
                  <div className={cn(
                    "font-bold text-white",
                    isMobile ? "text-lg" : "text-xl"
                  )}>
                    {stat.value}
                  </div>
                  <div className={cn(
                    "text-purple-200",
                    RESPONSIVE_TYPOGRAPHY.bodySmall
                  )}>
                    {stat.label}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Main Unified Billing Interface Component
 */
export const UnifiedBillingInterface: React.FC<UnifiedBillingInterfaceProps> = ({
  paymentMethods,
  invoices,
  stats,
  onAddPaymentMethod,
  onMakePayment,
  onDownloadInvoice,
  onSetDefaultPayment,
  loading,
  error,
  onRetry
}) => {
  const { isMobile } = useResponsive();
  const [activeTab, setActiveTab] = useState<'overview' | 'invoices' | 'methods'>('overview');

  const pendingInvoices = useMemo(() => 
    invoices.filter(inv => inv.status === 'pending' || inv.status === 'overdue'),
    [invoices]
  );

  const paidInvoices = useMemo(() => 
    invoices.filter(inv => inv.status === 'paid'),
    [invoices]
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <ParentLoadingState 
          message="Loading billing information..." 
          size="md"
          variant="skeleton"
          className="h-40"
        />
        <ParentSkeletonList items={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(
        "space-y-6",
        RESPONSIVE_SPACING.section.mobile
      )}>
        <Card className="rounded-2xl border-red-200 bg-red-50/50">
          <CardContent className={RESPONSIVE_SPACING.card.padding}>
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <div>
                <h3 className={cn(
                  "font-semibold text-red-900",
                  RESPONSIVE_TYPOGRAPHY.h4
                )}>
                  Unable to load billing information
                </h3>
                <p className={cn(
                  "text-red-700",
                  RESPONSIVE_TYPOGRAPHY.bodySmall
                )}>
                  {error}
                </p>
              </div>
            </div>
            {onRetry && (
              <Button 
                onClick={onRetry}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100"
                style={{ minHeight: TOUCH_TARGETS.minSize }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn(
      "space-y-6",
      RESPONSIVE_SPACING.section.mobile
    )}>
      {/* Stats Header */}
      <BillingStatsHeader stats={stats} />

      {/* Mobile Tab Navigation */}
      {isMobile && (
        <div className="flex bg-gray-100 rounded-2xl p-1">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'invoices', label: 'Invoices' },
            { key: 'methods', label: 'Payment' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={cn(
                "flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-colors",
                activeTab === tab.key
                  ? "bg-white text-purple-700 shadow-sm"
                  : "text-gray-600"
              )}
              style={{ minHeight: TOUCH_TARGETS.minSize }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Content based on tab or all content for desktop */}
      <AnimatePresence mode="wait">
        {(!isMobile || activeTab === 'overview') && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Quick Actions */}
            <Card className="rounded-2xl border border-gray-200">
              <CardHeader>
                <CardTitle className={RESPONSIVE_TYPOGRAPHY.h3}>
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={cn(
                  "grid gap-3",
                  isMobile ? "grid-cols-1" : "grid-cols-2 lg:grid-cols-3"
                )}>
                  <Button 
                    className="w-full justify-start"
                    onClick={onAddPaymentMethod}
                    style={{ minHeight: TOUCH_TARGETS.minSize }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Payment Method
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full justify-start"
                    style={{ minHeight: TOUCH_TARGETS.minSize }}
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    View All Invoices
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full justify-start"
                    style={{ minHeight: TOUCH_TARGETS.minSize }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Statements
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Pending Invoices */}
            {pendingInvoices.length > 0 && (
              <Card className="rounded-2xl border border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className={cn(
                    "text-amber-800",
                    RESPONSIVE_TYPOGRAPHY.h3
                  )}>
                    Action Required
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingInvoices.slice(0, 3).map((invoice) => (
                      <InvoiceCard
                        key={invoice.id}
                        invoice={invoice}
                        onPay={() => onMakePayment(invoice.id)}
                        onDownload={() => onDownloadInvoice(invoice.id)}
                      />
                    ))}
                    {pendingInvoices.length > 3 && (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={() => setActiveTab('invoices')}
                        style={{ minHeight: TOUCH_TARGETS.minSize }}
                      >
                        View {pendingInvoices.length - 3} More Pending
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}

        {(!isMobile || activeTab === 'invoices') && (
          <motion.div
            key="invoices"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className={RESPONSIVE_TYPOGRAPHY.h3}>
                  Invoice History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <InvoiceCard
                      key={invoice.id}
                      invoice={invoice}
                      onPay={invoice.status === 'pending' || invoice.status === 'overdue' 
                        ? () => onMakePayment(invoice.id) 
                        : undefined}
                      onDownload={() => onDownloadInvoice(invoice.id)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {(!isMobile || activeTab === 'methods') && (
          <motion.div
            key="methods"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <Card className="rounded-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className={RESPONSIVE_TYPOGRAPHY.h3}>
                    Payment Methods
                  </CardTitle>
                  <Button 
                    onClick={onAddPaymentMethod}
                    size="sm"
                    style={{ minHeight: TOUCH_TARGETS.minSize }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className={cn(
                  "grid gap-4",
                  isMobile ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"
                )}>
                  {paymentMethods.map((method) => (
                    <PaymentMethodCard
                      key={method.id}
                      method={method}
                      onSetDefault={() => onSetDefaultPayment(method.id)}
                    />
                  ))}
                </div>
                
                {paymentMethods.length === 0 && (
                  <div className="text-center py-12">
                    <Wallet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No payment methods
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Add a payment method to get started
                    </p>
                    <Button 
                      onClick={onAddPaymentMethod}
                      style={{ minHeight: TOUCH_TARGETS.minSize }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Payment Method
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UnifiedBillingInterface;